import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { addMute, fetchMutes, removeMute, type MuteRecord } from '../api';
import { useAuth } from '../auth/useAuth';
import { reportNonFatal } from '../observability/reportNonFatal';
import type { ParsedItem } from '../types';

// Per-user "Luettu / hiljennä" marks. Same shape as useBookmarks (load on
// account, optimistic toggle, stable `ids` / `toggle`), with two differences:
//   - Rollback is local: only the toggled id is flipped back, so a failed
//     toggle can't be followed by a second (revert-fetch) failure.
//   - A toggle for an id whose previous request is still in flight is
//     ignored, so a double-click can't send POST + DELETE out of order and
//     leave the server disagreeing with the UI.
//   - Toggles made while the initial GET is in flight are replayed onto the
//     loaded list instead of being overwritten by it.
export function useMutes() {
  const { account, acquireApiToken } = useAuth();
  const [records, setRecords] = useState<MuteRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Hold acquireApiToken in a ref so effects + callbacks can stay stable
  // (same Teams-SSO probe churn guard as useBookmarks).
  const acquireApiTokenRef = useRef(acquireApiToken);
  acquireApiTokenRef.current = acquireApiToken;

  // While a GET is in flight: id → muted state the user asked for since the
  // GET started. The GET may have been served before those writes landed, so
  // its list is merged with these rather than trusted outright. null when no
  // load is in progress.
  const loadOverridesRef = useRef<Map<string, boolean> | null>(null);

  useEffect(() => {
    if (!account) {
      setRecords([]);
      return;
    }
    let cancelled = false;
    const overrides = new Map<string, boolean>();
    loadOverridesRef.current = overrides;
    // Only this load's own map may be cleared — a newer load owns the ref
    // once this one is cancelled.
    const release = () => {
      if (loadOverridesRef.current === overrides) loadOverridesRef.current = null;
    };
    setLoading(true);
    void (async () => {
      try {
        const token = await acquireApiTokenRef.current();
        const list = await fetchMutes(token);
        if (cancelled) return;
        release();
        setRecords(mergeOverrides(list, overrides));
        setError(null);
      } catch (err) {
        if (cancelled) return;
        release();
        setError(err instanceof Error ? err.message : String(err));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
      release();
    };
  }, [account]);

  // Memoised so Dashboard's central mute filter only recomputes when the
  // mute set actually changes.
  const ids = useMemo(() => new Set(records.map((r) => r.itemId)), [records]);

  // `toggle` reads the latest ids through a ref instead of closing over them,
  // so its identity never changes and a click handler bound to a stale render
  // still sees the current state.
  const idsRef = useRef(ids);
  idsRef.current = ids;
  const pendingRef = useRef(new Set<string>());

  const toggle = useCallback(async (item: ParsedItem): Promise<void> => {
    const id = item.id;
    if (pendingRef.current.has(id)) return;
    pendingRef.current.add(id);
    const wasMuted = idsRef.current.has(id);
    // Remember the intent if the initial GET is still loading. Captured once
    // so a failure only clears the entry from the load it was recorded in.
    const overrides = loadOverridesRef.current;
    const previousOverride = overrides?.get(id);
    overrides?.set(id, !wasMuted);

    const apply = (mute: boolean) =>
      setRecords((prev) => {
        const without = prev.filter((r) => r.itemId !== id);
        return mute ? [...without, { itemId: id, mutedAt: new Date().toISOString() }] : without;
      });

    // Optimistic flip, local rollback on failure.
    apply(!wasMuted);
    try {
      const token = await acquireApiTokenRef.current();
      if (wasMuted) await removeMute(token, id);
      else await addMute(token, id);
      setError(null);
    } catch (err) {
      apply(wasMuted);
      // Server state is unchanged by this toggle: restore whatever an earlier
      // (successful) toggle during the same load recorded, if anything.
      if (previousOverride === undefined) overrides?.delete(id);
      else overrides?.set(id, previousOverride);
      const message = err instanceof Error ? err.message : String(err);
      setError(message);
      // The failure is swallowed here (toggle resolves), so beacon it.
      reportNonFatal(err, 'useMutes.toggle', { action: wasMuted ? 'unmute' : 'mute' });
    } finally {
      pendingRef.current.delete(id);
    }
  }, []);

  const isMuted = useCallback((itemId: string) => ids.has(itemId), [ids]);

  return {
    ids,
    isMuted,
    toggle,
    loading,
    error,
    count: records.length,
    records,
  };
}

// Apply toggles recorded during a load onto the list that load returned:
// muted ids are added (keeping the server's mutedAt when present), unmuted
// ids are removed.
function mergeOverrides(list: MuteRecord[], overrides: Map<string, boolean>): MuteRecord[] {
  if (overrides.size === 0) return list;
  const merged = list.filter((r) => overrides.get(r.itemId) !== false);
  const present = new Set(merged.map((r) => r.itemId));
  const now = new Date().toISOString();
  for (const [itemId, muted] of overrides) {
    if (muted && !present.has(itemId)) merged.push({ itemId, mutedAt: now });
  }
  return merged;
}

// "Näytä kaikki" — whether muted items stay visible (dimmed). Per browser,
// default off. Storage access is wrapped because it throws in some embedded
// hosts (Teams on locked-down devices, private modes); the toggle then just
// doesn't persist.
export const SHOW_MUTED_STORAGE_KEY = 'aiWhatsNew.showMuted';

function readShowMuted(): boolean {
  try {
    return localStorage.getItem(SHOW_MUTED_STORAGE_KEY) === '1';
  } catch {
    return false;
  }
}

export function useShowMuted(): [boolean, () => void] {
  const [showMuted, setShowMuted] = useState(readShowMuted);
  const toggleShowMuted = useCallback(() => {
    const next = !showMuted;
    try {
      if (next) localStorage.setItem(SHOW_MUTED_STORAGE_KEY, '1');
      else localStorage.removeItem(SHOW_MUTED_STORAGE_KEY);
    } catch {
      // Not persisted; the in-memory toggle still works for this session.
    }
    setShowMuted(next);
  }, [showMuted]);
  return [showMuted, toggleShowMuted];
}
